import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { Zap, Target, Shield, Sliders, Headphones, Activity, TrendingUp } from "lucide-react";
import { RONIN_LOGO } from "@/lib/brand";
import { BrandLogo } from "@/components/BrandMark";
import { api } from "@/lib/api";

const MODES = [
  { icon: Zap, name: "Shadowboxing", body: "Perfect your mechanics, balance, and footwork anywhere, anytime. No equipment required." },
  { icon: Target, name: "Heavy Bag", body: "Build devastating power and conditioning with high-intensity combination drills on the bag." },
  { icon: Shield, name: "Defense", body: "Sharpen your reflexes with dedicated rounds focused on slipping, rolling, pulling, and blocking." },
  { icon: Sliders, name: "Custom Mode", body: "Take full control. Build your own rounds, adjust the pace, and focus on the specific skills you want to develop." },
];

const LEVELS = [
  { name: "Beginner", body: "Learn the fundamental number system, basic stance, and core mechanics at a manageable pace." },
  { name: "Intermediate", body: "Increase the speed and complexity of combinations while integrating fluid defensive movements." },
  { name: "Advanced", body: "Push your limits with rapid-fire, high-volume combinations, advanced footwork, and professional-level pacing to reach your ultimate flow state." },
];

export default function RoninPromo() {
  const navigate = useNavigate();
  const [promo, setPromo] = useState({ google_play_url: "", app_store_url: "", promo_enabled: true });

  useSEO({
    title: "Ronin Box — Audio-Driven Boxing & Conditioning App | Better Through Boxing",
    description: "Ronin Box brings professional technical boxing coaching to your headphones. Real-time combination, defense, and footwork cues across 4 training modes and every skill level. Download on Google Play & the App Store.",
    path: "/ronin-box",
    keywords: [
      "boxing app", "boxing workout app", "shadow boxing app", "audio boxing coach",
      "heavy bag app", "boxing conditioning", "Ronin Box", "Better Through Boxing",
    ],
  });

  useEffect(() => {
    api.get("/ronin-box/app-promo")
      .then(({ data }) => {
        setPromo(data);
        // If the coach flips promo off, fall back to the full browser app.
        if (data && data.promo_enabled === false) navigate("/ronin-box/app", { replace: true });
      })
      .catch(() => { /* keep defaults — badges show "coming soon" */ });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="ronin-promo-page">
      <SiteHeader />

      {/* Hero */}
      <section className="relative border-b border-[#27272A] bg-[#0A0A0A] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ background: "radial-gradient(circle at 80% 15%, #FF331F 0%, transparent 55%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-3">
            <div className="flex items-center gap-5 mb-8" data-testid="promo-logos">
              <img src={RONIN_LOGO} alt="Ronin Box logo" className="h-24 w-24 sm:h-28 sm:w-28 object-contain rounded-sm" />
              <div className="flex flex-col gap-1 border-l border-[#27272A] pl-5">
                <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">A program by</span>
                <BrandLogo className="h-8 w-auto object-contain" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Better Through Boxing</span>
              </div>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl text-white uppercase tracking-tight leading-[0.9]" data-testid="promo-h1">
              Get better<br /><span className="text-[#FF331F]">through boxing.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-zinc-400 leading-relaxed">
              Ronin Box is the ultimate audio-driven boxing and conditioning app. Whether you&apos;re lacing up for the first time or fine-tuning fight-camp conditioning, it brings professional technical coaching directly to your headphones.
            </p>
            <div className="mt-8">
              <StoreBadges promo={promo} />
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="border border-[#27272A] bg-[#050505] p-6 rounded-sm">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-4">The Ronin Box engine</p>
              <FeatureRow icon={Headphones} title="Real-time audio cues" sub="Calls out combinations, defense & footwork as you move" />
              <FeatureRow icon={Activity} title="Health device sync" sub="Track heart rate, calories & progress as you train" />
              <FeatureRow icon={Target} title="4 dynamic training modes" sub="Shadow · Heavy Bag · Defense · Custom" />
              <FeatureRow icon={TrendingUp} title="Scales to every level" sub="Beginner to advanced, automatically" />
            </div>
          </div>
        </div>
      </section>

      {/* Engine blurb */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-14 md:py-20 text-center">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-3">Professional coaching, in your ear</p>
        <h2 className="font-display text-3xl sm:text-4xl text-white uppercase tracking-tight">The Ronin Box audio engine</h2>
        <p className="mt-5 text-zinc-400 leading-relaxed text-base md:text-lg">
          At the core of the app is the Ronin Box audio engine, calling out combinations, defensive movements, and footwork cues in real-time. Sync your workouts with your favorite health-tracking devices to monitor heart rate, calorie burn, and overall progress as you train.
        </p>
      </section>

      {/* Training modes */}
      <section className="border-t border-[#27272A] bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
          <div className="mb-10 text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">🥊 4 Dynamic Training Modes</p>
            <h2 className="font-display text-3xl sm:text-4xl text-white uppercase tracking-tight">Tailor every session</h2>
            <p className="text-sm text-zinc-400 mt-2 max-w-2xl mx-auto">Built for your exact environment and goals.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="promo-modes">
            {MODES.map((m) => (
              <div key={m.name} className="border border-[#27272A] bg-[#050505] p-6 rounded-sm hover:border-[#FF331F]/50 transition-colors">
                <m.icon className="h-6 w-6 text-[#FF331F] mb-4" />
                <h3 className="font-display text-xl text-white uppercase tracking-tight mb-2">{m.name}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skill levels */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16">
        <div className="mb-10 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">📈 For Every Skill Level</p>
          <h2 className="font-display text-3xl sm:text-4xl text-white uppercase tracking-tight">Every mode scales to you</h2>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl mx-auto">Ronin Box automatically matches your current experience.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4" data-testid="promo-levels">
          {LEVELS.map((l, i) => (
            <div key={l.name} className="border border-[#27272A] bg-[#0A0A0A] p-6 rounded-sm">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-3">0{i + 1}</div>
              <h3 className="font-display text-2xl text-white uppercase tracking-tight mb-2">{l.name}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{l.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-[#27272A] bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 text-center">
          <h2 className="font-display text-3xl sm:text-5xl text-white uppercase tracking-tight leading-[0.95]">
            Ready to reach your<br /><span className="text-[#FF331F]">flow state?</span>
          </h2>
          <p className="mt-5 text-zinc-400 max-w-xl mx-auto">Download Ronin Box and bring the corner with you — anywhere you train.</p>
          <div className="mt-8 flex justify-center">
            <StoreBadges promo={promo} />
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function StoreBadges({ promo }) {
  const play = promo?.google_play_url;
  const apple = promo?.app_store_url;
  return (
    <div className="flex flex-wrap gap-3" data-testid="promo-store-badges">
      <StoreBadge
        href={play}
        testid="promo-badge-google-play"
        top="Get it on"
        name="Google Play"
        icon={<GooglePlayIcon />}
      />
      <StoreBadge
        href={apple}
        testid="promo-badge-app-store"
        top="Download on the"
        name="App Store"
        icon={<AppleIcon />}
      />
    </div>
  );
}

function StoreBadge({ href, top, name, icon, testid }) {
  const available = !!href;
  const inner = (
    <>
      <span className="shrink-0">{icon}</span>
      <span className="flex flex-col leading-tight text-left">
        <span className="text-[9px] uppercase tracking-widest text-zinc-400">{available ? top : "Coming soon to"}</span>
        <span className="text-base font-bold text-white -mt-0.5">{name}</span>
      </span>
    </>
  );
  const base = "inline-flex items-center gap-3 border rounded-md px-4 py-2.5 min-w-[168px] transition-colors";
  if (available) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${base} border-[#3F3F46] bg-black hover:border-[#FF331F]`}
        data-testid={testid}
      >
        {inner}
      </a>
    );
  }
  return (
    <div
      className={`${base} border-[#27272A] bg-[#0A0A0A] opacity-70 cursor-default`}
      data-testid={`${testid}-soon`}
      title="Link coming soon"
    >
      {inner}
    </div>
  );
}

function GooglePlayIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3.6 2.3c-.3.3-.5.7-.5 1.3v16.8c0 .6.2 1 .5 1.3l.1.1L13 12.1v-.2L3.7 2.2l-.1.1z" fill="#00D0FF" />
      <path d="M16.3 15.4l-3.3-3.3v-.2l3.3-3.3.1.1 3.9 2.2c1.1.6 1.1 1.7 0 2.3l-3.9 2.2h-.1z" fill="#FFD500" />
      <path d="M16.4 15.3L13 12 3.6 21.7c.4.4 1 .4 1.7.1l11.1-6.5" fill="#FF3A44" />
      <path d="M16.4 8.7L5.3 2.2c-.7-.4-1.3-.3-1.7.1L13 12l3.4-3.3z" fill="#00E676" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden="true">
      <path d="M16.4 12.7c0-2 1.6-3 1.7-3-1-1.4-2.4-1.6-2.9-1.6-1.2-.1-2.4.7-3 .7-.6 0-1.6-.7-2.6-.7-1.3 0-2.6.8-3.3 2-1.4 2.4-.4 6 1 8 .7 1 1.4 2 2.4 2 1 0 1.3-.6 2.5-.6s1.5.6 2.5.6 1.7-.9 2.3-1.9c.7-1.1 1-2.1 1-2.2-.1 0-2-.8-2.1-3zM14.6 6.3c.5-.7.9-1.6.8-2.5-.8 0-1.7.5-2.3 1.2-.5.6-.9 1.5-.8 2.4.9.1 1.8-.4 2.3-1.1z" />
    </svg>
  );
}

function FeatureRow({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="h-4 w-4 text-[#FF331F] mt-0.5 shrink-0" />
      <div>
        <div className="text-sm text-white font-bold">{title}</div>
        <div className="text-xs text-zinc-500">{sub}</div>
      </div>
    </div>
  );
}
